/*
 * Names of the categories an accepted targeted rerun is updating, for the run sync layer.
 * Written by the rerun mutation when the API accepts (useBudgetMutations), read by
 * useBudgetAiRunSync when the run ends, so the completion toast names them whichever panel
 * is open.
 */

type DocumentId = string | number | null | undefined;

const requestKey = (projectId: string | number, documentId: DocumentId) =>
  `${projectId}:${documentId ?? ""}`;

/*
 * Names of the categories an accepted targeted rerun is updating, kept until that run ends so
 * the completion toast can name them. Any full rerun clears them, so it never reuses an old list.
 */

const namesKey = (projectId: string | number, documentId: DocumentId) =>
  `rivetai:budget-targeted-rerun:${requestKey(projectId, documentId)}`;

export function saveTargetedRerunCategories(
  projectId: string | number,
  documentId: DocumentId,
  categoryNames: string[]
): void {
  try {
    localStorage.setItem(namesKey(projectId, documentId), JSON.stringify(categoryNames));
  } catch {
    // Storage full or blocked: the completion toast falls back to the generic text.
  }
}

export function clearTargetedRerunCategories(
  projectId: string | number,
  documentId: DocumentId
): void {
  try {
    localStorage.removeItem(namesKey(projectId, documentId));
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** Reads and clears the saved names. Null when the finished run was not a targeted one. */
export function takeTargetedRerunCategories(
  projectId: string | number,
  documentId: DocumentId
): string[] | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(namesKey(projectId, documentId));
  } catch {
    return null;
  }
  clearTargetedRerunCategories(projectId, documentId);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const names = parsed.filter((name): name is string => typeof name === "string" && !!name);
    return names.length > 0 ? names : null;
  } catch {
    return null;
  }
}
